import { PlusCircle } from '@phosphor-icons/react';

import { Button } from '../ui/Button';
import { QuickCreateChoiceChip } from './QuickCreateChoiceChip';
import { QuickCreateFieldGroup } from './QuickCreateFieldGroup';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';

/** `.pen` `ds/form/inline-create--collapsed` / `--expanded`. */
export function QuickCreateInlineCreateField({
  expanded,
  onExpand,
  onCancel,
  title,
  description,
  nameLabel,
  name,
  onNameChange,
  namePlaceholder,
  nameHint,
  allocationOptions,
  allocation,
  onAllocationChange,
  onCreate,
  submitLabel,
  cancelLabel,
  isPending,
  error,
}) {
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={onExpand}
        aria-label={title}
        className={cn(
          'flex w-full items-center justify-center gap-space-2 rounded-ds-md border border-sage-border bg-sage-tint p-space-4 transition-colors',
          'hover:bg-sage-100 dark:hover:bg-sage-900/40',
        )}
      >
        <PlusCircle size={18} className="shrink-0 text-sage-on-tint" aria-hidden />
        <span className="type-body font-semibold text-sage-on-tint">{title}</span>
      </button>
    );
  }

  const canCreate = Boolean(name.trim()) && !isPending;

  return (
    <div className="flex w-full flex-col gap-space-3 rounded-ds-lg border border-border bg-muted p-space-4">
      <div className="flex items-center gap-space-2">
        <PlusCircle size={17} className="shrink-0 text-sage-on-tint" aria-hidden />
        <p className="type-body font-semibold text-card-foreground">{title}</p>
      </div>
      {description ? <p className="type-label font-normal text-muted-foreground">{description}</p> : null}

      <QuickCreateFieldGroup
        label={nameLabel}
        value={name}
        onChange={onNameChange}
        placeholder={namePlaceholder}
        hint={nameHint}
      />

      {allocationOptions ? (
        <div className="flex flex-col gap-space-2">
          <span className="type-label text-foreground">{pt.quickCreate.allocationLabel}</span>
          <div className="flex flex-wrap items-center gap-space-2">
            {allocationOptions.map((option) => (
              <QuickCreateChoiceChip
                key={option.value}
                label={option.label}
                dotClassName={option.dotClass}
                selected={allocation === option.value}
                onSelect={() => onAllocationChange(option.value)}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-space-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={!canCreate}
          onClick={onCreate}
          className="flex-1 justify-center"
        >
          {isPending ? pt.common.loading : submitLabel}
        </Button>
        {onCancel ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="flex-1 justify-center"
          >
            {cancelLabel || pt.common.cancel}
          </Button>
        ) : null}
      </div>

      {error ? (
        <p className="type-body-sm text-error" role="alert">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </p>
      ) : null}
    </div>
  );
}
